import { AlertCircle, Clock3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger, Skeleton } from '@shigo-ui/ui';

import { DashboardScrollNavigation } from './DashboardScrollNavigation';
import { DashboardCanvas } from '@/components/dashboard/DashboardCanvas';
import { DashboardDateControl } from '@/components/dashboard/DashboardDateControl';
import { dateTimeFormat } from '../../../lib/format';
import { dashboardT } from '../../../lib/messages';
import { resolveDashboardWidgetRange } from '../dateRange';
import { defaultDashboardDocument } from '../defaultDashboard';
import { defaultSuperAdminDashboardDocument } from '../defaultSuperAdminDashboard';
import {
  dashboardRangeKey,
  useDashboardLastImportQuery,
  useDashboardOverviewQueries,
} from '../hooks';
import { DashboardScope, DashboardWidgetType } from '../model';
import { useDashboardRouteFilters } from '../useDashboardRouteFilters';
import { mainDashboardCanvasRegistry } from '../widgetRegistry';

export function DashboardPage({ scope = DashboardScope.Main }: { scope?: DashboardScope }) {
  const { filters, setRange } = useDashboardRouteFilters();
  const document =
    scope === DashboardScope.SuperAdmin
      ? defaultSuperAdminDashboardDocument
      : defaultDashboardDocument;
  const widgets = document.widgets.filter(
    (widget) =>
      widget.type !== DashboardWidgetType.PerformanceChart ||
      Boolean(resolveDashboardWidgetRange(widget.dateScope, filters.range).preset),
  );
  const widgetRanges = widgets.map((widget) =>
    resolveDashboardWidgetRange(widget.dateScope, filters.range),
  );
  const rangesByWidgetId = new Map(
    widgets.map((widget, index) => [
      widget.id,
      widgetRanges[index] as (typeof widgetRanges)[number],
    ]),
  );
  const queryMap = useDashboardOverviewQueries(widgetRanges, scope);
  const lastImportQuery = useDashboardLastImportQuery();
  const [renderedAt, setRenderedAt] = useState(Date.now);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRenderedAt(Date.now());
    }, 60_000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  const sectionLabels = {
    [DashboardWidgetType.PerformanceChart]: dashboardT('dashboard.performance.title'),
    [DashboardWidgetType.PublisherPerformance]: dashboardT('dashboard.workspace.publishers'),
    [DashboardWidgetType.NetworkPerformance]: dashboardT('dashboard.workspace.networks'),
    [DashboardWidgetType.DemandPartners]: dashboardT('dashboard.workspace.partners'),
    [DashboardWidgetType.DemandIntegrations]: dashboardT('dashboard.workspace.integrations'),
    [DashboardWidgetType.SrpmRanking]: dashboardT('dashboard.srpmRanking.title'),
    [DashboardWidgetType.Metric]: '',
  };
  return (
    <main
      id="dashboard-overview"
      tabIndex={-1}
      style={{ scrollMarginTop: '7rem' }}
      className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 space-y-6 py-5"
    >
      <section className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <DashboardDateControl showDayNavigation value={filters.range} onChange={setRange} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Freshness
            value={lastImportQuery.data}
            error={lastImportQuery.isError}
            loading={lastImportQuery.isLoading}
            now={renderedAt}
          />
        </div>
      </section>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_max-content] xl:gap-4">
        <div className="min-w-0">
          <DashboardCanvas
            document={{ ...document, widgets }}
            registry={mainDashboardCanvasRegistry}
            runtime={{
              rangesByWidgetId,
              queriesByRangeKey: queryMap,
              rangeKey: dashboardRangeKey,
            }}
          />
        </div>
        <DashboardScrollNavigation
          sections={widgets
            .filter((widget) => widget.type !== DashboardWidgetType.Metric)
            .map((widget) => ({
              id:
                widget.type === DashboardWidgetType.PerformanceChart
                  ? 'dashboard-overview'
                  : `dashboard-widget-${widget.id}`,
              label: sectionLabels[widget.type],
            }))}
        />
      </div>
    </main>
  );
}

function Freshness({
  value,
  error,
  loading,
  now,
}: {
  value?: string | undefined;
  error: boolean;
  loading: boolean;
  now: number;
}) {
  if (loading) return <Skeleton className="h-9 w-40 rounded-md" />;
  if (error || !value)
    return (
      <div className="border-destructive/30 bg-destructive/5 text-destructive inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        {dashboardT('dashboard.freshness.error')}
      </div>
    );
  const minutes = Math.max(0, Math.floor((now - new Date(value).getTime()) / 60_000));
  const tone = freshnessTone(minutes);
  const warningKey = freshnessWarningKey(minutes);
  const timestampLabel = dashboardT('dashboard.freshness.updatedAt', {
    value: dateTimeFormat({ dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)),
  });
  const details = (
    <>
      <span className="block">{timestampLabel}</span>
      {warningKey ? <span className="mt-1 block">{dashboardT(warningKey)}</span> : null}
    </>
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${tone} inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs`}
        >
          <Clock3 className="h-4 w-4" aria-hidden="true" />
          {dashboardT('dashboard.freshness.minutes', { count: minutes })}
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label={timestampLabel}
        side="top"
        align="end"
        className="w-auto max-w-[min(20rem,calc(100vw-2rem))] px-3 py-1.5 text-xs"
      >
        {details}
      </PopoverContent>
    </Popover>
  );
}

function freshnessTone(minutes: number) {
  if (minutes >= 120) return 'border-destructive/30 bg-destructive/5 text-destructive';
  if (minutes >= 60) return 'border-warning/30 bg-warning/5 text-warning';
  return 'border-border bg-card text-muted-foreground';
}

function freshnessWarningKey(minutes: number) {
  if (minutes >= 120) return 'dashboard.freshness.warning.twoHours' as const;
  if (minutes >= 60) return 'dashboard.freshness.warning.oneHour' as const;
  return null;
}
